import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
} from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { combineLatest, filter, firstValueFrom, map, shareReplay, switchMap, take } from "rxjs";

import { CollectionService } from "@bitwarden/admin-console/common";
import { OrganizationService } from "@bitwarden/common/admin-console/abstractions/organization/organization.service.abstraction";
import { PolicyService } from "@bitwarden/common/admin-console/abstractions/policy/policy.service.abstraction";
import {
  CollectionData,
  CollectionDetailsResponse,
} from "@bitwarden/common/admin-console/models/collections";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { FeatureFlag } from "@bitwarden/common/enums/feature-flag.enum";
import { ConfigService } from "@bitwarden/common/platform/abstractions/config/config.service";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { Utils } from "@bitwarden/common/platform/misc/utils";
import { CipherId, CollectionId, OrganizationId } from "@bitwarden/common/types/guid";
import { CipherArchiveService } from "@bitwarden/common/vault/abstractions/cipher-archive.service";
import { CipherService } from "@bitwarden/common/vault/abstractions/cipher.service";
import { FolderService } from "@bitwarden/common/vault/abstractions/folder/folder.service.abstraction";
import { CipherType } from "@bitwarden/common/vault/enums";
import { RestrictedItemTypesService } from "@bitwarden/common/vault/services/restricted-item-types.service";
import { CipherViewLike } from "@bitwarden/common/vault/utils/cipher-view-like-utils";
import { filterOutNullish } from "@bitwarden/common/vault/utils/observable-utilities";
import {
  ButtonModule,
  CalloutModule,
  DialogService,
  IconTileComponent,
  LinkModule,
  PopoverModule,
} from "@bitwarden/components";
import { isGuid } from "@bitwarden/guid";
import { PolicyType } from "@bitwarden/sdk-internal";
import { I18nPipe, safeProvider } from "@bitwarden/ui-common";
import {
  AddEditFolderDialogComponent,
  AddItemDialogComponent,
  AddItemDialogResult,
  ASSIGN_COLLECTIONS_DIALOG,
  BULK_DELETE_DIALOG,
  CipherRowMenuHandlers,
  CipherRowMenuService,
  COLLECTION_DIALOG,
  CollectionOptionsMenuComponent,
  copyPresentation$,
  DecryptionFailureDialogComponent,
  DEFAULT_COPY_PRESENTATION,
  DefaultCipherFormConfigService,
  MyFolderBreadcrumbsComponent,
  MyFolderMenuComponent,
  NewCipherMenuComponent,
  CardGridComponent,
  VaultBreadcrumbsComponent,
  VaultItemsTableComponent,
  VaultItemsTableRowAction,
  VaultNavService,
  VaultOrganizationUserNotificationsComponent,
  VaultBatchActionComponent,
  VaultBatchBarService,
  VaultRemountOnDirective,
  ALL_ITEMS_SCOPE,
  cipherInScope,
  collectionInScope,
  collectionMenuTargetForScope,
  folderNameForScope,
  hasMultipleVaults,
  organizationNameForScope,
  organizationInScope,
  organizationVaultPage,
  OrganizationVaultPage,
  resolveVaultScope,
  routeVaultScope,
  scopedFolderId,
  sharedFoldersCommands,
  vaultScopeHeaderTile,
  vaultScopeTitle,
  scopedSharedFolderId,
  scopeKey,
  MY_ITEMS_ROUTE,
  sharedFolderNameForScope,
  VaultScope,
  VaultScopeType,
  defaultUserCollectionId,
  DefaultVaultItemsTransferService,
  VaultItemsTransferService,
  CoachmarkComponent,
  CoachmarkService,
} from "@bitwarden/vault";

import {
  CollectionDialogAction,
  openCollectionDialog,
} from "../../admin-console/organizations/shared/components/collection-dialog";
import { HeaderModule } from "../../layouts/header/header.module";
import { ImportDialogComponent } from "../../tools/import/import-dialog.component";
import { AssignCollectionsWebDialogAdapter } from "../components/assign-collections/assign-collections-web-dialog.adapter";
import { WebVaultItemActionsService } from "../services/vault-item-actions.service";
import { WebVaultPromptService } from "../services/web-vault-prompt.service";
import { ItemDeepLink, ItemDeepLinkAction, itemDeepLinkFrom } from "../utils/item-deep-link";

import { BulkDeleteDialogWebAdapter } from "./bulk-action-dialogs/bulk-delete-dialog-web.adapter";
import { CollectionWebDialogAdapter } from "./shared-folders/collection-web-dialog.adapter";
import { VaultBannersComponent } from "./vault-banners/vault-banners.component";
import { VaultOnboardingComponent } from "./vault-onboarding/vault-onboarding.component";

function selectionScopeDetail(scope: VaultScope): string {
  if (scope.type === VaultScopeType.Organization) {
    return scope.organizationId;
  }
  if (scope.type === VaultScopeType.Folder) {
    return scope.folderId;
  }
  return "";
}

/**
 * The web individual vault built on the shared {@link VaultItemsTableComponent}, which owns its own
 * search, filter chips, and sorting — so this page has no filter sidebar.
 *
 * Every side-nav destination renders this one component, scoped by the `:vaultId` route segment —
 * see `VaultScope`.
 */
@Component({
  selector: "app-vault-next",
  templateUrl: "./vault-next.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "tw-flex tw-flex-col tw-h-full tw-min-h-0",
  },
  imports: [
    ButtonModule,
    CalloutModule,
    CoachmarkComponent,
    I18nPipe,
    HeaderModule,
    LinkModule,
    RouterLink,
    NewCipherMenuComponent,
    PopoverModule,
    VaultBannersComponent,
    VaultBatchActionComponent,
    VaultBreadcrumbsComponent,
    MyFolderBreadcrumbsComponent,
    MyFolderMenuComponent,
    CollectionOptionsMenuComponent,
    IconTileComponent,
    VaultItemsTableComponent,
    VaultOnboardingComponent,
    VaultOrganizationUserNotificationsComponent,
    VaultRemountOnDirective,
    CardGridComponent,
  ],
  providers: [
    safeProvider({ provide: DefaultCipherFormConfigService, useAngularDecorators: true }),
    safeProvider({ provide: WebVaultItemActionsService, useAngularDecorators: true }),
    safeProvider({ provide: WebVaultPromptService, useAngularDecorators: true }),
    safeProvider({
      provide: VaultItemsTransferService,
      useClass: DefaultVaultItemsTransferService,
      useAngularDecorators: true,
    }),
    VaultBatchBarService,
    safeProvider({
      provide: ASSIGN_COLLECTIONS_DIALOG,
      useClass: AssignCollectionsWebDialogAdapter,
      useAngularDecorators: true,
    }),
    safeProvider({
      provide: BULK_DELETE_DIALOG,
      useClass: BulkDeleteDialogWebAdapter,
      useAngularDecorators: true,
    }),
    safeProvider({
      provide: COLLECTION_DIALOG,
      useClass: CollectionWebDialogAdapter,
      useAngularDecorators: true,
    }),
  ],
})
export class VaultNextComponent implements OnInit {
  private readonly accountService = inject(AccountService);
  private readonly cipherRowMenuService = inject(CipherRowMenuService);
  private readonly cipherService = inject(CipherService);
  private readonly collectionService = inject(CollectionService);
  private readonly configService = inject(ConfigService);
  private readonly dialogService = inject(DialogService);
  private readonly folderService = inject(FolderService);
  private readonly itemActions = inject(WebVaultItemActionsService);
  private readonly organizationService = inject(OrganizationService);
  private readonly restrictedItemTypesService = inject(RestrictedItemTypesService);
  private readonly vaultNavService = inject(VaultNavService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly cipherArchiveService = inject(CipherArchiveService);
  private readonly i18nService = inject(I18nService);
  private readonly batchBarService = inject(VaultBatchBarService);
  private readonly router = inject(Router);
  private readonly policyService = inject(PolicyService);
  private readonly webVaultPromptService = inject(WebVaultPromptService);
  private readonly userId$ = this.accountService.activeAccount$.pipe(getUserId);

  protected readonly coachmarkService = inject(CoachmarkService);

  protected readonly importCoachmarkOpen = computed(
    () => this.coachmarkService.activeStepId() === "importData",
  );

  /**
   * Onboarding prompts are the page's to start. {@link WebVaultPromptService} sequences them so
   * only one shows at a time.
   */
  ngOnInit(): void {
    void this.webVaultPromptService.conditionallyPromptUser();
  }

  private readonly routeParams = toSignal(this.activatedRoute.paramMap);

  private readonly routeData = toSignal(this.activatedRoute.data);

  private readonly routeScope = computed(() =>
    routeVaultScope(this.routeParams(), this.routeData()),
  );

  private readonly vaultNav = toSignal(
    this.userId$.pipe(switchMap((userId) => this.vaultNavService.viewModel$(userId))),
  );

  /**
   * The vault the side nav has scoped this page to, and the shared folder within it the URL has
   * drilled into. `vaultScopeGuard` has already turned away any segment that names no vault, so an
   * unresolvable one here means the guard was bypassed — show everything rather than an empty page.
   */
  protected readonly vaultScope = computed(
    () => resolveVaultScope(this.routeScope(), this.vaultNav()) ?? ALL_ITEMS_SCOPE,
  );

  protected readonly defaultCollectionId = computed(() => {
    const scope = this.vaultScope();
    if (scope.type !== VaultScopeType.Organization) {
      return undefined;
    }
    return defaultUserCollectionId(scope.organizationId, this.vaultNav());
  });

  /** Only a shared folder trails a breadcrumb; every other page reads as a plain title. */
  protected readonly showBreadcrumbs = computed(
    () =>
      organizationVaultPage(this.vaultScope(), this.vaultNav()) ===
      OrganizationVaultPage.SharedFolder,
  );

  protected readonly headerTile = computed(() =>
    vaultScopeHeaderTile(this.vaultScope(), this.vaultNav()),
  );

  protected readonly parsedVaultScope = computed(() => this.routeScope() ?? ALL_ITEMS_SCOPE);

  /**
   * The scope key the vault table's filter state belongs to. Keyed off the parsed scope rather
   * than {@link vaultScope}, which resolves a second time as the nav loads.
   */
  protected readonly filterScopeKey = computed(() => scopeKey(this.parsedVaultScope()));

  protected readonly collectionSelected = computed(() => {
    const seg = scopedSharedFolderId(this.parsedVaultScope());
    return seg != null && seg !== MY_ITEMS_ROUTE;
  });

  /**
   * Every item the user can see, in every state. Which of trashed, archived, and active items a
   * page shows is the scope's call — see {@link cipherInScope} — so this narrows by nothing but
   * the restricted item types, which no scope may show.
   */
  private readonly allCiphers$ = this.userId$.pipe(
    switchMap((userId) =>
      combineLatest([
        // Emits null until the first decrypt completes. Reads the with-partials stream so
        // PAM-gated rows stay listed — a member has to see the item to request access to it.
        this.cipherService.cipherListViewsIncludingRestricted$(userId).pipe(filterOutNullish()),
        this.restrictedItemTypesService.restricted$,
      ]),
    ),
    map(([ciphers, restricted]) =>
      ciphers.filter(
        (cipher) => !this.restrictedItemTypesService.isCipherRestricted(cipher, restricted),
      ),
    ),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  /**
   * The items that could not be decrypted. `cipherListViews$` drops them, so they reach the page
   * only through here — see {@link ciphers} and {@link showDecryptionFailureDialog}.
   */
  private readonly failedCiphers$ = this.userId$.pipe(
    switchMap((userId) => this.cipherService.failedToDecryptCiphers$(userId)),
    filterOutNullish(),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  /** `undefined` until the ciphers stream first emits, which is what drives {@link loading}. */
  private readonly loadedCiphers = toSignal(this.allCiphers$);

  private readonly allCiphers = computed<CipherViewLike[]>(() => this.loadedCiphers() ?? []);

  private readonly failedCiphers = toSignal(this.failedCiphers$, { initialValue: [] });

  /**
   * Opens once per visit, and names only the failures the page in view can show — the same
   * `cipherInScope` narrowing {@link ciphers} applies.
   */
  private readonly showDecryptionFailureDialog = combineLatest([
    this.failedCiphers$,
    toObservable(this.vaultScope),
  ])
    .pipe(
      map(([ciphers, scope]) => ciphers.filter((cipher) => cipherInScope(cipher, scope))),
      filter((ciphers) => ciphers.length > 0),
      take(1),
      takeUntilDestroyed(),
    )
    .subscribe((ciphers) =>
      DecryptionFailureDialogComponent.open(this.dialogService, {
        cipherIds: ciphers.map((cipher) => cipher.id as CipherId),
      }),
    );

  /**
   * Every item in the account's active vaults. The banners and onboarding speak to the account as
   * a whole rather than to the page, so they read this instead of the scoped rows — an empty My
   * vault should not make an account that has organization items look brand new.
   */
  protected readonly activeCiphers = computed<CipherViewLike[]>(() =>
    this.allCiphers().filter((cipher) => cipherInScope(cipher, ALL_ITEMS_SCOPE)),
  );

  /**
   * The rows for the table: {@link allCiphers} plus {@link failedCiphers}, narrowed to the scope.
   *
   * A failed item still carries its organization, collections, and deleted date — none of which
   * needed decrypting — so `cipherInScope` places it without any special case. It is left out of
   * {@link activeCiphers}, which the banners and onboarding read.
   */
  protected readonly ciphers = computed<CipherViewLike[]>(() => {
    const scope = this.vaultScope();
    return [...this.failedCiphers(), ...this.allCiphers()].filter((cipher) =>
      cipherInScope(cipher, scope),
    );
  });

  protected readonly loading = computed(() => this.loadedCiphers() === undefined);

  protected readonly folders = toSignal(
    this.userId$.pipe(
      switchMap((userId) => this.folderService.folderViews$(userId)),
      // `folderViews$` appends a "no folder" pseudo-folder with an empty id. The table has its own
      // NO_FOLDER sentinel for that option, so passing it through would duplicate it and defeat the
      // table's own "user has no folders" check.
      map((folders) => folders.filter((folder) => folder.id != null && folder.id !== "")),
    ),
    { initialValue: [] },
  );

  protected readonly collections = toSignal(
    this.userId$.pipe(switchMap((userId) => this.collectionService.decryptedCollections$(userId))),
    { initialValue: [] },
  );

  protected readonly organizations = toSignal(
    this.userId$.pipe(switchMap((userId) => this.organizationService.memberOrganizations$(userId))),
    { initialValue: [] },
  );

  /**
   * The collections the table resolves its Shared folders column and chip from, and the card grid
   * derives its tree from. The chip lists whatever this holds rather than deriving its options from
   * the rows, so a scoped page has to narrow it or it offers folders none of its items could be in.
   *
   * Narrowed to the vault only, never to the shared folder in view: an item belongs to as many
   * shared folders as it was assigned to, so a row in the folder being viewed may live in others
   * too — narrowing this would drop those from its Shared folders column and leave the chip unable
   * to offer them. The breadcrumb tree needs the whole vault for the same reason: the folder it
   * drills into has to be findable in the tree.
   *
   * The unscoped {@link collections} still back the row actions, which assign an item to any
   * collection the user can reach — not just the ones this page shows.
   */
  protected readonly scopedCollections = computed(() => {
    const scope = this.vaultScope();
    return this.collections().filter((collection) => collectionInScope(collection, scope));
  });

  /** The organizations the table names its Vault column and chip from — see {@link scopedCollections}. */
  protected readonly scopedOrganizations = computed(() => {
    const scope = this.vaultScope();
    return this.organizations().filter((organization) => organizationInScope(organization, scope));
  });

  /** Scopes the table's search index to the organization, for an organization vault. */
  protected readonly scopedOrganizationId = computed(() => {
    const scope = this.vaultScope();
    return scope.type === VaultScopeType.Organization ? scope.organizationId : undefined;
  });

  /**
   * The shared folder the scope has drilled into, prefilled onto a new item — `undefined` unless
   * it names an actual collection rather than the {@link MY_ITEMS_ROUTE} sentinel, which
   * `resolveVaultScope` has yet to resolve to an id while the nav is still loading.
   */
  protected readonly scopedCollectionId = computed(() => {
    const collectionId = scopedSharedFolderId(this.vaultScope());
    return collectionId != null && isGuid(collectionId)
      ? (collectionId as CollectionId)
      : undefined;
  });

  private readonly collectionMenuTarget = computed(() =>
    collectionMenuTargetForScope(this.vaultScope(), this.scopedCollections(), this.organizations()),
  );

  protected readonly currentCollection = computed(() => this.collectionMenuTarget()?.collection);

  protected readonly currentOrganization = computed(
    () => this.collectionMenuTarget()?.organization,
  );

  /**
   * The vault-scope display-name facts {@link EmptyVaultComponent} needs for its copy, relayed
   * through `vault-items-table` untouched — the table itself has no notion of vault scope.
   *
   * Gated by {@link showItemCreation}: Trash and Archive are not vaults an "Add item" message
   * makes sense for, even for an account these facts would otherwise resolve non-empty for.
   */
  protected readonly emptyVaultOrganizationName = computed(() =>
    this.showItemCreation()
      ? organizationNameForScope(this.vaultScope(), this.vaultNav())
      : undefined,
  );

  protected readonly hasMultipleVaults = computed(
    () => this.showItemCreation() && hasMultipleVaults(this.vaultNav()),
  );

  protected readonly emptySharedFolderName = computed(() =>
    this.showItemCreation()
      ? sharedFolderNameForScope(this.vaultScope(), this.scopedCollections())
      : undefined,
  );

  protected readonly emptyFolderName = computed(() =>
    this.showItemCreation() ? folderNameForScope(this.vaultScope(), this.folders()) : undefined,
  );

  protected readonly scopedFolderId = computed(() => scopedFolderId(this.vaultScope()));

  protected readonly canCreateCollections = computed(() => {
    const scope = this.vaultScope();

    // The "Add item" menu offers a "New collection" action only for organization vaults or when viewing all their items
    if (scope.type !== VaultScopeType.Organization && scope.type !== VaultScopeType.AllItems) {
      return false;
    }

    return this.organizations()?.some((o) => o.canCreateNewCollections && !o.isProviderUser);
  });

  /**
   * Whether the page offers the toolbar's Import and New item actions. New items cannot be created
   * with a trashed or archived status and would "disappear" after creation on those views.
   */
  protected readonly showItemCreation = computed(() => {
    const { type } = this.vaultScope();
    return type !== VaultScopeType.Trash && type !== VaultScopeType.Archive;
  });

  private readonly subscriptionEndedMessaging = toSignal(
    this.userId$.pipe(
      switchMap((userId) => this.cipherArchiveService.showSubscriptionEndedMessaging$(userId)),
      take(1),
    ),
    { initialValue: false },
  );

  protected readonly showSubscriptionEndedMessaging = computed(
    () => this.vaultScope().type === VaultScopeType.Archive && this.subscriptionEndedMessaging(),
  );

  protected readonly title = computed(
    () =>
      vaultScopeTitle(this.vaultScope(), this.i18nService, this.vaultNav()) ??
      folderNameForScope(this.vaultScope(), this.folders()),
  );

  private readonly configureBatchBar = effect(() => {
    const collections = this.collections();
    const hasCiphers = this.ciphers().length > 0;
    const scope = this.vaultScope();
    const inTrash = scope.type === VaultScopeType.Trash;
    const scopedCollectionId =
      scope.type === VaultScopeType.Organization ? scope.collectionId : undefined;
    const activeCollectionId = collections.find((c) => c.id === scopedCollectionId)?.id;
    untracked(() =>
      this.batchBarService.setConfig({
        isOrgVault: false,
        allCollections: collections,
        hasCiphers,
        inTrash,
        activeCollectionId,
      }),
    );
  });

  /** Used to ensure the selection is cleared when the side nav rescopes the page */
  private readonly lastScopeKey = signal<string | undefined>(undefined);

  private readonly clearSelectionOnScopeChange = effect(() => {
    // `resolveVaultScope` builds a fresh object each run, so compare by value, not reference.
    const scope = this.vaultScope();
    const key = `${scope.type}:${selectionScopeDetail(scope)}`;
    untracked(() => {
      if (this.lastScopeKey() !== undefined && this.lastScopeKey() !== key) {
        this.batchBarService.clearSelection();
      }
      this.lastScopeKey.set(key);
    });
  });

  protected readonly copyPresentation = toSignal(copyPresentation$(), {
    initialValue: DEFAULT_COPY_PRESENTATION,
  });

  private readonly rowMenuHandlers = computed<CipherRowMenuHandlers<CipherViewLike>>(() => ({
    edit: (item) => this.itemActions.edit(item),
    clone: (item) => this.itemActions.clone(item),
    assignToCollections: (item) => this.itemActions.assignToCollections(item, this.collections()),
  }));

  protected readonly rowActions = computed<VaultItemsTableRowAction<CipherViewLike>[]>(() =>
    this.cipherRowMenuService.getRowActions<CipherViewLike>(
      this.collections(),
      this.rowMenuHandlers(),
    ),
  );

  /** Whether the `OrganizationDataOwnership` policy applies to the active user. */
  protected readonly orgRequiresDataOwnership = toSignal(
    this.userId$.pipe(
      switchMap((userId) =>
        this.policyService.policyAppliesToUser$(PolicyType.OrganizationDataOwnership, userId),
      ),
    ),
    { initialValue: false },
  );

  /** The account's active My vault items, for the Vault chip's My vault count on an org page. */
  protected readonly myVaultItemCount = computed(
    () => this.activeCiphers().filter((cipher) => cipher.organizationId == null).length,
  );

  /**
   * Clicking an item's name opens the read-only view, matching the legacy vault — the dialog offers
   * its own Edit toggle from there, while the `edit` row action goes straight to the form.
   *
   * Bound as an input, so it must be a stable reference rather than a method: a new function on each
   * change detection pass would churn the table's name column.
   */
  protected readonly itemAction = (item: CipherViewLike): Promise<void> =>
    this.itemActions.view(item);

  private readonly queryParams = toSignal(this.activatedRoute.queryParamMap);

  /** The item the URL asks the page to open, if any — see {@link itemDeepLinkFrom}. */
  private readonly itemDeepLink = computed(() => itemDeepLinkFrom(this.queryParams()));

  /** The `<cipherId>:<action>` of the deep link last dispatched, so it is dispatched only once. */
  private readonly dispatchedDeepLink = signal<string | undefined>(undefined);

  /**
   * Opens the item named by the deep link on the URL — see {@link itemDeepLinkFrom}.
   *
   * The dispatch waits for the items to decrypt: `WebVaultItemActionsService` reads the item from
   * storage, and an item that has not loaded yet reads the same as one that does not exist.
   *
   * It also holds while a dialog is open, because `VaultItemDialogComponent` writes these same
   * params each time the user toggles view and edit — and it dispatches each link once, because
   * the params the dialog leaves behind would otherwise reopen it.
   */
  private readonly openDeepLinkedItem = effect(() => {
    const link = this.itemDeepLink();
    const loading = this.loading();
    const dialogOpen = this.itemActions.itemDialogOpen();

    untracked(() => {
      if (link == null) {
        this.dispatchedDeepLink.set(undefined);
        return;
      }

      if (loading || dialogOpen) {
        return;
      }

      const dispatched = `${link.cipherId}:${link.action}`;
      if (this.dispatchedDeepLink() === dispatched) {
        return;
      }
      this.dispatchedDeepLink.set(dispatched);

      void this.dispatchDeepLink(link);
    });
  });

  private async dispatchDeepLink(link: ItemDeepLink): Promise<void> {
    if (this.failedCiphers().some((cipher) => cipher.id === link.cipherId)) {
      await this.itemActions.showDecryptionFailure(link.cipherId);
      return;
    }

    switch (link.action) {
      case ItemDeepLinkAction.Edit:
        await this.itemActions.editById(link.cipherId);
        break;
      case ItemDeepLinkAction.Clone:
        await this.itemActions.cloneById(link.cipherId);
        break;
      case ItemDeepLinkAction.ShowFailedToDecrypt:
        await this.itemActions.showDecryptionFailure(link.cipherId);
        break;
      case ItemDeepLinkAction.View:
        await this.itemActions.viewById(link.cipherId);
        break;
    }
  }

  /** Handles `vault-new-cipher-menu`'s `cipherAdded`, emitted by its legacy per-type dropdown. */
  protected async addCipher(cipherType: CipherType): Promise<void> {
    await this.itemActions.add(cipherType, {
      organizationId: this.scopedOrganizationId(),
      collectionId: this.scopedCollectionId(),
      folderId: this.scopedFolderId(),
    });
  }

  /**
   * Handles `vault-new-cipher-menu`'s `onAddItemDialog`, which it only emits once
   * `PM32009NewItemTypes` is on.
   */
  protected async openAddItemDialog(eventOrigin: "empty" | "toolbar"): Promise<void> {
    let toolbarOptions = {};
    // The empty state should only give the user options that allow them to populate that
    // empty state. Therefore folders and shared folders should only be included when the dialog
    // is opened from the toolbar.
    if (eventOrigin === "toolbar") {
      toolbarOptions = {
        canCreateFolder: true,
        canCreateCollection: this.canCreateCollections(),
      };
    }

    const dialogRef = AddItemDialogComponent.open(this.dialogService, {
      canCreateCipher: true,
      canCreateSshKey: true,
      canCreateFolder: false,
      canCreateCollection: false,
      ...toolbarOptions,
    });
    const result = await firstValueFrom(dialogRef.closed);
    if (result == null) {
      return;
    }

    if (result.result === AddItemDialogResult.Cipher) {
      await this.itemActions.add(result.cipherType, {
        organizationId: this.scopedOrganizationId(),
        collectionId: this.scopedCollectionId(),
        folderId: this.scopedFolderId(),
      });
    } else if (result.result === AddItemDialogResult.Folder) {
      this.addFolder();
    } else if (result.result === AddItemDialogResult.Collection) {
      await this.addCollection();
    }
  }

  /** Handles `vault-new-cipher-menu`'s `folderAdded`, emitted by its legacy dropdown. */
  protected addFolder(): void {
    AddEditFolderDialogComponent.open(this.dialogService);
  }

  /** Handles `vault-new-cipher-menu`'s `collectionAdded`, emitted by its legacy dropdown. */
  protected async addCollection(): Promise<void> {
    const eligibleOrganizations = this.organizations()
      .filter((o) => o.canCreateNewCollections && !o.isProviderUser)
      .sort(Utils.getSortFunction(this.i18nService, "name"));
    if (eligibleOrganizations.length === 0) {
      return;
    }

    const defaultOrganizationId =
      eligibleOrganizations.find((o) => o.id === this.scopedOrganizationId())?.id ??
      eligibleOrganizations[0].id;

    const dialogRef = openCollectionDialog(this.dialogService, {
      data: {
        organizationId: defaultOrganizationId,
        parentCollectionId: this.scopedCollectionId(),
        showOrgSelector: true,
        limitNestedCollections: true,
      },
    });
    const result = await firstValueFrom(dialogRef.closed);
    if (result?.action !== CollectionDialogAction.Saved) {
      return;
    }

    if (result.collection) {
      const userId = await firstValueFrom(this.userId$);
      await this.collectionService.upsert(
        new CollectionData(result.collection as CollectionDetailsResponse),
        userId,
      );
    }
  }

  private readonly lastViewedFolder = signal<
    { organizationId: OrganizationId; collectionId: CollectionId } | undefined
  >(undefined);

  private readonly navigateAwayIfFolderVanishes = effect(() => {
    const collection = this.currentCollection();
    const organizationId = this.scopedOrganizationId();
    const collectionId = this.scopedCollectionId();

    untracked(() => {
      if (collection != null) {
        this.lastViewedFolder.set(
          organizationId != null && collectionId != null
            ? { organizationId, collectionId }
            : undefined,
        );
        return;
      }

      const last = this.lastViewedFolder();
      if (
        last != null &&
        last.organizationId === organizationId &&
        last.collectionId === collectionId
      ) {
        this.lastViewedFolder.set(undefined);
        void this.router.navigate(sharedFoldersCommands(organizationId), { replaceUrl: true });
      }
    });
  });

  protected async openImport(): Promise<void> {
    if (await this.configService.getFeatureFlag(FeatureFlag.ImportUpgrade)) {
      // TODO: (PM-41469) this drops the org/collection scope the legacy branch below pre-fills.
      // The new picker has no defined way to receive it yet (its `continue` output isn't wired
      // to anything) — Tools Team to implement this before finalizing Import UI/UX upgrades
      await this.router.navigate(["/tools/import"]);
      return;
    }

    ImportDialogComponent.open(
      this.dialogService,
      this.scopedOrganizationId(),
      this.scopedCollectionId(),
    );
  }
}
